import React from 'react';
import { useMediaQuery } from 'react-responsive';
import { Grid, Text, Button, useTheme } from 'mintel-react-ui';
import { laptop, tablet } from 'mintel-tokens';

import * as styles from './ErrorPage.scss';
// eslint-disable-next-line import/no-unresolved
import * as svgs from './svgs/*.svg';

const buttonTypes = {
  BACK: 'BACK',
  REFRESH: 'REFRESH',
  HOMEPAGE: 'HOMEPAGE',
  CONTACT_ACCOUNT_MANAGER: 'CONTACT_ACCOUNT_MANAGER',
};
const errors: {
  [key: number]: {
    heading: JSX.Element | string;
    error: string;
    message: string;
    buttons: string[];
    widths: Record<string, string>;
  };
} = {
  404: {
    heading: 'Lost? This isn’t right.',
    error: '404 - NOT FOUND',
    message:
      "We can't seem to find the page you were looking for. It could have " +
      'been an error, or the page no longer exists.',
    buttons: [buttonTypes.BACK, buttonTypes.HOMEPAGE],
    widths: { desktop: '374', tablet: '274', mobile: '194' },
  },
  403: {
    heading: (
      <>
        Access denied, for now...
        <br />
        How about we resolve this for you?
      </>
    ),
    error: '403 - FORBIDDEN',
    message:
      'We’re sorry but your current permission settings limit access to this page. If you get in contact with your Account Manager $accountManager they can grant you access.',
    buttons: [buttonTypes.BACK, buttonTypes.CONTACT_ACCOUNT_MANAGER],
    widths: { desktop: '459', tablet: '320', mobile: '232' },
  },
  500: {
    heading: 'We’re sorry, page not found.',
    error: '500 - INTERNAL SERVER ERROR',
    message:
      "We're having trouble loading this page. Try refreshing or come back later.",
    buttons: [buttonTypes.BACK, buttonTypes.REFRESH],
    widths: { desktop: '410', tablet: '264', mobile: '207' },
  },
  503: {
    heading: 'Site under maintenance.',
    error: '503 - SERVICE UNAVAILABLE',
    message:
      "We're sorry but the site is currently under maintenance. Please come back later.",
    buttons: [buttonTypes.BACK],
    widths: { desktop: '410', tablet: '264', mobile: '207' },
  },
};

const BaseErrorPage = ({
  errorCode = 500,
  userName = '',
  accountManager,
}: {
  errorCode?: number;
  userName?: string;
  accountManager?: string;
}): JSX.Element => {
  const { heading, error, message, buttons, widths } = errors[errorCode];

  let device = 'desktop';
  if (useMediaQuery({ maxWidth: laptop })) device = 'tablet';
  if (useMediaQuery({ maxWidth: parseInt(tablet, 10) - 1 })) device = 'mobile';
  const isDesktop = device === 'desktop';

  const Heading = isDesktop ? Text.Heading1 : Text.Heading3;
  const ErrorText = isDesktop ? Text.Heading4 : Text.Heading5;
  const Message = isDesktop ? Text.Body : Text.Body2;

  const theme = useTheme();

  const goBackText = 'Go Back';

  const BackButton =
    buttons.length <= 1
      ? () => (
          <Button.ActionButton handleClick={() => window.history.back()}>
            {goBackText}
          </Button.ActionButton>
        )
      : () => (
          <Button.CancelButton handleClick={() => window.history.back()}>
            {goBackText}
          </Button.CancelButton>
        );

  return (
    <Grid className={styles.root}>
      <Grid.Row>
        <Grid.Col width={12} tablet={6}>
          <div className={`${styles.errorSvg}`}>
            <img
              src={svgs[`${theme}${errorCode}`]}
              width={widths[device]}
              alt=""
            />
          </div>
        </Grid.Col>
        <Grid.Col width={12} tablet={6}>
          <div className={styles.content}>
            <div className={styles.heading}>
              <Heading>{heading}</Heading>
            </div>
            <div className={styles.error}>
              <ErrorText>{error}</ErrorText>
            </div>
            <div className={styles.message}>
              <Message>
                {message.replace(
                  '$accountManager ',
                  accountManager ? `(${accountManager}) ` : '',
                )}
              </Message>
            </div>
            <div className={styles.buttonRow}>
              {buttons.includes(buttonTypes.BACK) && <BackButton />}
              {buttons.includes(buttonTypes.HOMEPAGE) && (
                <Button.CallToActionButton href="/">
                  Return to Homepage
                </Button.CallToActionButton>
              )}
              {buttons.includes(buttonTypes.REFRESH) && (
                <Button.ActionButton
                  handleClick={() => window.location.reload()}
                >
                  Refresh
                </Button.ActionButton>
              )}
              {buttons.includes(buttonTypes.CONTACT_ACCOUNT_MANAGER) && (
                <Button.CallToActionButton
                  disabled={!accountManager}
                  href={encodeURI(
                    `mailto:${accountManager}` +
                      `?subject=${userName} would like to access a page outside of their subscription` +
                      `&body=I'm interested in extending my subscription.\n${window.location.href}`,
                  )}
                >
                  Contact Account Manager
                </Button.CallToActionButton>
              )}
            </div>
            {errorCode === 403 && (
              <Text.Body>
                <a href="https://help.mintel.com/en/articles/5142018-find-support">
                  Or, visit our Help Centre
                </a>
              </Text.Body>
            )}
          </div>
        </Grid.Col>
      </Grid.Row>
    </Grid>
  );
};

export default BaseErrorPage;
